import {
  GoogleSignInButton,
  type GoogleSignInButtonAlign,
} from './GoogleSignInButton';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

interface AuthStatusProps {
  compact?: boolean;
  signInAlign?: GoogleSignInButtonAlign;
}

export function AuthStatus({
  compact = false,
  signInAlign = 'end',
}: AuthStatusProps): JSX.Element | null {
  const { user, signOut } = useAuth();

  if (!user) {
    return <GoogleSignInButton align={signInAlign} />;
  }

  return (
    <div className="flex items-center gap-2">
      {user.pictureUrl ? (
        <img
          src={user.pictureUrl}
          alt=""
          className="h-8 w-8 rounded-full border border-gray-200 dark:border-gray-700"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-700 dark:bg-gray-800 dark:text-gray-200">
          {getInitials(user.name)}
        </div>
      )}
      {!compact && (
        <div className="hidden min-w-0 sm:block">
          <p className="max-w-[12rem] truncate text-sm font-semibold text-gray-900 dark:text-white">
            {user.name}
          </p>
          {user.role === 'admin' && (
            <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
              App admin
            </p>
          )}
        </div>
      )}
      {user.role === 'admin' && (
        <Link
          to="/admin"
          className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          Admin
        </Link>
      )}
      <button
        type="button"
        onClick={signOut}
        className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
      >
        Sign out
      </button>
    </div>
  );
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0][0]?.toUpperCase() ?? '?';
  return `${words[0][0] ?? ''}${words[words.length - 1][0] ?? ''}`.toUpperCase();
}
